/**
 * Decimal-degree coordinate parsing for the location picker.
 *
 * Supported format: two decimal numbers (dot as decimal separator) separated by
 * either a comma or whitespace, with optional leading/trailing whitespace and an
 * optional sign.
 * Examples: "51.5847, 4.7765", "51.5847,4.7765", "51.5847 4.7765",
 *           " -33.8688, 151.2093 "
 *
 * Both numbers must carry a decimal point. This is deliberate: it lets a plain
 * space act as the lat/lng separator without misreading two bare integers (e.g.
 * a house number like "5 10") as coordinates. Decimal degrees always have a
 * fractional part in practice — a whole-degree value must be written as "4.0".
 * This matches the approach taken by established coordinate parsers (e.g.
 * geo-coordinates-parser).
 *
 * European comma-as-decimal is deliberately NOT supported: "51,5847 4,7765"
 * is ambiguous because the comma also serves as the lat/lng separator.
 */
// Optional sign, digits, mandatory decimal point + digits. Dot decimal only.
// The mandatory fractional part is what makes whitespace-separated parsing safe.
var DECIMAL_NUMBER = '[-+]?\\d+\\.\\d+';
// Lat/lng separator: a comma (with optional surrounding whitespace) OR one or
// more whitespace characters.
var SEPARATOR = '(?:\\s*,\\s*|\\s+)';
// Two decimal numbers, separated as above, surrounded by optional leading/
// trailing whitespace. Nothing else.
var COORDINATE_PATTERN = new RegExp("^\\s*(".concat(DECIMAL_NUMBER, ")").concat(SEPARATOR, "(").concat(DECIMAL_NUMBER, ")\\s*$"));
/**
 * Attempts to parse a raw input string as a decimal-degree coordinate pair.
 *
 * Returns:
 *  - `{ kind: 'coordinates', lat, lng }` — valid, in-range coordinates
 *  - `{ kind: 'invalid' }` — looks like coordinates but out of range
 *  - `{ kind: 'none' }` — does not look like coordinates; fall through to address search
 */
export function parseCoordinates(input) {
    var match = COORDINATE_PATTERN.exec(input);
    if (!match) {
        return { kind: 'none' };
    }
    var lat = parseFloat(match[1]);
    var lng = parseFloat(match[2]);
    // parseFloat on a regex-validated number should never produce NaN,
    // but guard defensively.
    if (isNaN(lat) || isNaN(lng)) {
        return { kind: 'none' };
    }
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        return { kind: 'invalid' };
    }
    return { kind: 'coordinates', lat: lat, lng: lng };
}
